import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { ArrowRight, Crosshair, FileCheck2, Layers, PencilRuler, SlidersHorizontal, CalendarClock, MessagesSquare, FolderCheck, LockKeyhole, Maximize2, MapPin, Database, Building2, LandPlot, Globe2 } from "lucide-react";
import Seo from "../lib/seo";
import { Reveal, MaskedLines, Stagger, StaggerItem, EASE } from "../components/motion";
import { Eyebrow, SectionHead, ContourBg, PrimaryCta, SecondaryCta, CheckList } from "../components/ui-bits";
import CadViewer from "../components/CadViewer";
import Marquee from "../components/Marquee";
import OrbitSection from "../components/OrbitShowcase";
import Faq from "../components/Faq";
import { SERVICES, PORTFOLIO, AUDIENCES } from "../data/services";

const FAQS = [
  { q: "What information is needed to begin a survey CAD drafting project?", a: "The essentials are the survey data you want drawn (coordinates, points, sketches, or prior drawings), your drafting standards or template where available, and written instructions describing the required deliverables. We review everything and confirm scope before production starts." },
  { q: "Can GeoDrafts work with our existing CAD standards and templates?", a: "Yes. Layer structures, naming conventions, title blocks, symbol libraries, and plot styles you supply are applied to the deliverables exactly as agreed — the drawings should look like they came from your own production team." },
  { q: "Which CAD and GIS file formats can you support?", a: "For CAD: DWG, DXF, and PDF as standard, with DGN available on request. For GIS: Shapefile, GeoPackage, GeoJSON, KML/KMZ, and CSV. Other formats can be discussed as part of the project scope." },
  { q: "Can you convert survey drawings into GIS datasets?", a: "Yes. CAD-to-GIS conversion is a core capability — supplied drawings are converted into structured GIS features with organized attributes, following the layer schema and coordinate specifications you provide." },
  { q: "Can you support recurring drafting requirements?", a: "Yes, subject to actual capacity. Many teams need steady production support alongside their own staff; we discuss expected volumes, scheduling, and scope arrangements before starting recurring work." },
  { q: "How are project scope, revisions, and delivery schedules agreed?", a: "In writing, before production begins. Each project confirmation defines the deliverables, assumptions, revision expectations, and delivery schedule so both sides share the same picture of what will be produced." },
  { q: "Does GeoDrafts conduct physical land surveys?", a: "No. GeoDrafts is a drafting and GIS production partner. Field surveying, licensed surveying, boundary determination, certification, and professional sign-off are separate responsibilities that remain with the licensed professionals you engage." },
  { q: "How can we share project files securely?", a: "Project files are exchanged through agreed secure file-sharing arrangements, and client materials are handled under agreed confidentiality terms. Share a secure link with your inquiry or ask us about the current preferred transfer method." },
];

const VALUES = [
  { icon: Crosshair, title: "Drawing Precision", desc: "Linework, annotation, and layout completed against defined project instructions — reviewed before delivery, sheet by sheet." },
  { icon: FileCheck2, title: "Consistent Documentation", desc: "Your templates, layer structures, and naming conventions applied across every drawing so files stay coherent project to project." },
  { icon: Layers, title: "Organized Spatial Data", desc: "GIS layers, attributes, and datasets structured for actual use — not just stored, but organized for the next workflow." },
];

const PRINCIPLES = [
  { icon: PencilRuler, title: "Detail-Oriented Production", desc: "Drawings and datasets prepared against defined project instructions." },
  { icon: SlidersHorizontal, title: "Client-Specific Standards", desc: "Layer structures, templates, naming conventions, and deliverable requirements followed as agreed." },
  { icon: CalendarClock, title: "Flexible Project Support", desc: "Support for individual assignments and ongoing production needs, subject to actual capacity." },
  { icon: MessagesSquare, title: "Clear Communication", desc: "Defined scope, deliverables, assumptions, and revision expectations." },
  { icon: FolderCheck, title: "Organized File Delivery", desc: "Consistent file structures and agreed output formats." },
  { icon: LockKeyhole, title: "Confidential Project Handling", desc: "Appropriate handling of client files and project information under agreed confidentiality arrangements." },
];

const AUDIENCE_ICONS = [Building2, LandPlot, Database, Globe2];

function Hero() {
  return (
    <section className="relative bg-white overflow-hidden" data-testid="home-hero">
      <ContourBg className="opacity-70" />
      <div className="absolute top-0 right-0 w-[42rem] h-[42rem] rounded-full bg-aqua/5 blur-3xl" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 lg:pt-36 pb-16 lg:pb-24 relative">
        <div className="grid lg:grid-cols-[1.02fr_0.98fr] gap-14 lg:gap-12 items-center">
          <div>
            <Reveal y={16}>
              <span className="inline-flex items-center gap-2.5 border border-lineblue bg-white/80 rounded-sm px-4 py-2">
                <MapPin className="h-3.5 w-3.5 text-aqua" aria-hidden="true" />
                <span className="font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-steel">Land Surveying &amp; Geospatial Solutions</span>
              </span>
            </Reveal>
            <MaskedLines
              lines={[
                { text: "Precise Survey Drafting.", cls: "text-navy" },
                { text: "Smarter Geospatial Data.", cls: "text-steel" },
              ]}
              delay={0.2}
              className="mt-6 text-4xl sm:text-5xl lg:text-[3.6rem] font-extrabold leading-[1.07] text-navy"
            />
            <Reveal delay={0.55}>
              <p className="mt-6 text-lg leading-relaxed text-mutedslate max-w-xl">
                GeoDrafts provides outsourced survey CAD drafting services for surveying professionals — detailed CAD drawings and GIS deliverables that transform field measurements and spatial information into organized, dependable project outputs.
              </p>
              <div className="mt-8 flex flex-wrap gap-4" data-testid="hero-cta-group">
                <PrimaryCta to="/request-a-quote" testid="hero-cta-quote">Request a Quote</PrimaryCta>
                <SecondaryCta to="#services" testid="hero-cta-services">Explore Our Services</SecondaryCta>
              </div>
              <p className="mt-7 text-sm italic text-mutedslate/90">
                Technical production support built around your project requirements and drafting standards.
              </p>
            </Reveal>
          </div>
          <div className="relative">
            <CadViewer />
          </div>
        </div>
      </div>
    </section>
  );
}

function Intro() {
  return (
    <section className="bg-paleslate py-20 lg:py-28 relative overflow-hidden" data-testid="home-intro">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 items-end mb-14">
          <SectionHead
            eyebrow="Why GeoDrafts"
            title="Technical Support for the Work Behind Every Survey"
          />
          <Reveal delay={0.1}>
            <p className="text-lg leading-relaxed text-mutedslate lg:pb-2">
              Well-organized survey drawings and spatial data help surveying professionals manage project documentation, communicate site information clearly, and prepare deliverables that downstream teams can rely on. As an outsourced land survey drafting and GIS services company, GeoDrafts handles that production workload — so in-house teams stay focused on fieldwork and review.
            </p>
          </Reveal>
        </div>
        <Stagger className="grid sm:grid-cols-3 gap-6">
          {VALUES.map((v) => (
            <StaggerItem key={v.title} className="h-full">
              <div className="h-full bg-white border border-lineblue rounded-md p-8 hover:shadow-[0_12px_32px_rgba(16,42,67,0.08)] hover:-translate-y-1 transition-all duration-300" data-testid="value-card">
                <v.icon className="h-7 w-7 text-aqua" strokeWidth={1.75} />
                <h3 className="mt-5 text-xl font-bold text-navy">{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-mutedslate">{v.desc}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section id="services" className="bg-white py-20 lg:py-28" data-testid="home-services">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <SectionHead
            eyebrow="Core Services"
            title="Land Surveying and GIS Expertise"
            lead="Six production services covering the drafting and geospatial work behind land surveying projects — each with its own dedicated page."
          />
          <Reveal delay={0.15}>
            <Link to="/request-a-quote" data-testid="services-cta-link" className="hidden lg:inline-flex items-center gap-2 text-sm font-bold text-steel hover:text-aqua transition-colors shrink-0">
              Discuss your scope <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
        <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.slice(0, 6).map((s) => (
            <StaggerItem key={s.slug} className="h-full">
              <Link
                to={`/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="group h-full flex flex-col bg-white border border-lineblue rounded-md p-8 hover:border-aqua/50 hover:shadow-[0_16px_40px_rgba(16,42,67,0.10)] hover:-translate-y-1 transition-all duration-300 relative overflow-hidden"
              >
                <span className="absolute top-0 left-0 h-1 w-0 bg-aqua group-hover:w-full transition-all duration-500" aria-hidden="true" />
                <s.icon className="h-7 w-7 text-steel" strokeWidth={1.75} />
                <h3 className="mt-5 text-xl font-bold text-navy leading-snug">{s.name}</h3>
                <p className="mt-3 text-sm leading-relaxed text-mutedslate flex-1">{s.short}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-steel group-hover:text-aqua transition-colors">
                  Learn More
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

const GIS_CAPABILITIES = [
  "GIS mapping and thematic map production",
  "Spatial data digitization",
  "CAD-to-GIS and GIS-to-CAD conversion",
  "Geospatial data cleanup and attribute organization",
  "Coordinate system and projection handling to supplied specifications",
  "Shapefile, GeoPackage, GeoJSON, KML/KMZ, and other agreed formats",
  "GIS map layouts and cartographic presentation",
  "Vector feature editing and layer organization",
];

function GisSpotlight() {
  return (
    <section className="bg-navy-deep py-20 lg:py-28 relative overflow-hidden" data-testid="home-gis">
      <ContourBg dark className="opacity-60" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHead
              dark
              eyebrow="GIS Services"
              title="Turn Spatial Data into Useful Information"
              lead="From source-data preparation to structured map outputs, GeoDrafts helps organize geospatial information into practical datasets and clear visual deliverables."
            />
            <Reveal delay={0.15} className="mt-9">
              <CheckList items={GIS_CAPABILITIES} dark cols={2} />
              <div className="mt-9 flex flex-wrap gap-4">
                <PrimaryCta to="/gis-mapping-services" dark testid="gis-cta-mapping">GIS Mapping Services</PrimaryCta>
                <SecondaryCta to="/gis-data-conversion" dark testid="gis-cta-conversion">GIS Data Conversion</SecondaryCta>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <div className="relative" data-testid="gis-visual">
              <div className="absolute -inset-3 bg-aqua/10 rounded-lg rotate-2" aria-hidden="true" />
              <img
                src="https://static.prod-images.emergentagent.com/jobs/7631f423-53c6-4462-98ac-35ec74ffec2d/images/d8aeca770683c67fa1409676c71421cea5dfd1289af2a0f4e033c95e3bcb6174.jpeg"
                alt="Exploded isometric illustration of stacked GIS map layers including contours, parcels, terrain and data points"
                loading="lazy"
                className="relative rounded-md border border-navy-light shadow-2xl w-full h-auto"
                data-testid="gis-layers-image"
              />
              <div className="absolute bottom-4 left-4 bg-navy-deep/90 backdrop-blur border border-aqua/30 rounded-sm px-4 py-2.5">
                <span className="font-mono text-[10px] tracking-[0.2em] text-aqua uppercase">[LAYERS: CONTOUR · PARCEL · TERRAIN · NETWORK]</span>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Audiences() {
  return (
    <section className="bg-white py-20 lg:py-28" data-testid="home-audiences">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="Who We Support"
          title="Built Around Surveying and Geospatial Workflows"
          lead="Supplementary production capacity for teams that need drawings and mapping deliverables prepared from their data — without replacing the professional responsibility of licensed surveyors or engineers."
        />
        <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
          {AUDIENCES.map((a, i) => {
            const Icon = AUDIENCE_ICONS[i];
            return (
              <StaggerItem key={a.title} className="h-full">
                <div className="h-full bg-paleslate border border-lineblue rounded-md p-7 hover:bg-white hover:shadow-[0_12px_32px_rgba(16,42,67,0.08)] transition-all duration-300" data-testid="audience-card">
                  <span className="font-mono text-[11px] text-aqua font-bold tracking-widest">0{i + 1}</span>
                  <Icon className="h-6 w-6 text-steel mt-3" strokeWidth={1.75} />
                  <h3 className="mt-4 text-lg font-bold text-navy leading-snug">{a.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-mutedslate">{a.desc}</p>
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>
        <Reveal className="mt-10">
          <p className="text-sm text-mutedslate">
            GeoDrafts supports licensed professionals — it does not replace their professional responsibility. <Link to="/industries" data-testid="audiences-industries-link" className="text-steel font-semibold hover:text-aqua underline underline-offset-4">See how teams work with us</Link>
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function WhyUs() {
  return (
    <section className="bg-paleslate py-20 lg:py-28 relative overflow-hidden" data-testid="home-why">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="Why Choose GeoDrafts?"
          title="A Practical Extension of Your Technical Team"
          lead="Six service principles that shape how every project is scoped, produced, and delivered."
        />
        <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {PRINCIPLES.map((p, i) => (
            <StaggerItem key={p.title} className="h-full">
              <div className="h-full bg-white border border-lineblue rounded-md p-8 relative overflow-hidden hover:shadow-[0_12px_32px_rgba(16,42,67,0.08)] transition-all duration-300" data-testid="principle-card">
                <span className="absolute -right-2 -top-4 font-display text-[5.5rem] font-extrabold text-paleslate select-none" aria-hidden="true">0{i + 1}</span>
                <div className="relative">
                  <p.icon className="h-6 w-6 text-aqua" strokeWidth={1.75} />
                  <h3 className="mt-4 text-lg font-bold text-navy">{p.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-mutedslate">{p.desc}</p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function Workflow() {
  const steps = [
    { title: "Share Project Inputs", desc: "Survey data, sketches, coordinates, drawings, GIS files, standards, and project instructions." },
    { title: "Confirm Scope", desc: "Outputs, file formats, drafting standards, assumptions, and schedule are reviewed and confirmed." },
    { title: "Prepare the Deliverables", desc: "The assigned CAD drafting or GIS production work is completed to the agreed scope." },
    { title: "Review and Refine", desc: "Deliverables are checked against instructions; revisions are handled per the project agreement." },
    { title: "Deliver Project Files", desc: "Final files are provided in the agreed formats with a clear, organized handover." },
  ];
  return (
    <section className="bg-white py-20 lg:py-28" data-testid="home-workflow">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="Our Workflow"
          title="A Clear Path from Source Data to Deliverables"
          lead="Five defined stages keep every project predictable — from the first shared file to the final organized handover."
        />
        <div className="mt-16 relative">
          <div className="hidden lg:block absolute top-[26px] left-0 right-0 h-px bg-lineblue" aria-hidden="true" />
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-6">
            {steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.12} className="relative">
                <div className="flex lg:flex-col items-start lg:items-stretch gap-5 lg:gap-0">
                  <div className="relative shrink-0">
                    <span className="relative z-10 inline-flex h-[52px] w-[52px] items-center justify-center rounded-full bg-navy text-aqua font-mono text-sm font-bold border-4 border-paleslate lg:border-white" data-testid={`workflow-step-${i + 1}`}>
                      0{i + 1}
                    </span>
                  </div>
                  <div className="lg:mt-6">
                    <h3 className="font-display font-bold text-navy leading-snug">{s.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-mutedslate">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal className="mt-14">
          <Link to="/our-process" data-testid="workflow-process-link" className="inline-flex items-center gap-2 text-sm font-bold text-steel hover:text-aqua transition-colors">
            See the full process <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function Showcase() {
  const [open, setOpen] = useState(null);
  return (
    <section className="bg-paleslate py-20 lg:py-28" data-testid="home-showcase">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="Project Showcase"
          title="Examples of Survey and GIS Deliverables"
          lead="Illustrative samples of the drawing types and datasets we prepare. These are representative examples, not client projects."
        />
        <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {PORTFOLIO.map((p, i) => (
            <StaggerItem key={p.title} className="h-full">
              <button
                onClick={() => setOpen(i)}
                data-testid={`portfolio-item-${i + 1}`}
                className="group relative block w-full text-left bg-white border border-lineblue rounded-md overflow-hidden hover:shadow-[0_16px_40px_rgba(16,42,67,0.12)] transition-all duration-300"
              >
                <div className="relative aspect-[3/2] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.alt}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                  />
                  <span className="absolute top-3 left-3 bg-white/95 border border-lineblue rounded-sm px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-steel">
                    {p.tag} · Illustrative sample
                  </span>
                  <span className="absolute bottom-3 right-3 bg-navy/85 text-white rounded-sm p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Maximize2 className="h-4 w-4" />
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-display font-bold text-navy group-hover:text-steel transition-colors">{p.title}</h3>
                </div>
              </button>
            </StaggerItem>
          ))}
        </Stagger>

        <Dialog open={open !== null} onOpenChange={(o) => !o && setOpen(null)}>
          <DialogContent className="max-w-4xl p-3 bg-white" data-testid="portfolio-lightbox">
            {open !== null && (
              <div>
                <img src={PORTFOLIO[open].image} alt={PORTFOLIO[open].alt} className="w-full h-auto rounded-sm" />
                <div className="flex items-center justify-between px-2 pt-3 pb-1">
                  <h3 className="font-display font-bold text-navy">{PORTFOLIO[open].title}</h3>
                  <span className="font-mono text-[10px] uppercase tracking-wider text-mutedslate">Illustrative sample</span>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
}

function FaqSection() {
  return (
    <section className="bg-white py-20 lg:py-28" data-testid="home-faq">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[0.9fr_1.4fr] gap-12">
        <SectionHead
          eyebrow="FAQ"
          title="Frequently Asked Questions"
          lead="Straight answers about scope, standards, formats, and how we work with surveying professionals."
        />
        <Faq items={FAQS} testPrefix="faq" />
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="relative bg-navy py-20 lg:py-28 overflow-hidden" data-testid="home-final-cta">
      <div className="absolute inset-0 bg-contours-dark" aria-hidden="true" />
      <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 h-96 w-[60rem] rounded-full bg-aqua/15 blur-3xl" aria-hidden="true" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
        <Reveal>
          <Eyebrow dark>Start a Project</Eyebrow>
          <h2 className="mt-5 text-4xl sm:text-5xl font-extrabold text-white leading-[1.1]" data-testid="final-cta-heading">
            Have Survey Data Ready for Drafting?
          </h2>
          <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-2xl mx-auto">
            Tell us about your CAD drafting or GIS requirements. We’ll review the project scope and discuss the deliverables needed for your workflow.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <PrimaryCta to="/request-a-quote" dark testid="final-cta-quote">Request a Quote</PrimaryCta>
            <SecondaryCta to="/contact" dark testid="final-cta-contact">Contact Our Team</SecondaryCta>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Seo
        title="Land Survey CAD Drafting Services & GIS Mapping | GeoDrafts"
        description="Outsourced survey CAD drafting services for surveying firms: boundary, topographic, ALTA/NSPS and as-built drafting, GIS mapping, PDF to CAD and point cloud conversion."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          name: "GeoDrafts",
          url: "https://geodrafts.com",
          image: "https://geodrafts.com/og-image.png",
          description: "Outsourced land survey CAD drafting and GIS production support for licensed surveying and engineering firms — all 50 US states, Canada, the UK and Australia.",
          email: "cad@geodrafts.com",
          telephone: "+1-938-307-1829",
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "sales",
            email: "cad@geodrafts.com",
            telephone: "+1-938-307-1829",
            availableLanguage: ["English"],
          },
          areaServed: [
            { "@type": "Country", name: "United States" },
            { "@type": "Country", name: "Canada" },
            { "@type": "Country", name: "United Kingdom" },
            { "@type": "Country", name: "Australia" },
          ],
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Survey CAD Drafting & GIS Services",
            itemListElement: SERVICES.slice(0, 8).map((s) => ({
              "@type": "Offer",
              itemOffered: { "@type": "Service", name: s.name, url: `https://geodrafts.com/${s.slug}` },
            })),
          },
        }}
      />
      <Hero />
      <Marquee />
      <OrbitSection />
      <Intro />
      <Services />
      <GisSpotlight />
      <Audiences />
      <WhyUs />
      <Workflow />
      <Showcase />
      <FaqSection />
      <FinalCta />
    </>
  );
}
